import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Paper,
  TextField,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Grid,
  Chip,
  Card,
  CardContent,
  Autocomplete
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import Cropper from "react-easy-crop";
import getCroppedImg from "../utils/cropImage";
import EditIcon from "@mui/icons-material/Edit";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AuthModal from '../components/AuthModal';
import { useParams } from 'react-router-dom';
import LazyImage from "../components/LazyImage"; // Add this import

const countries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda",
  "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain",
  "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica",
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
  "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada",
  "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras",
  "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan",
  "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
  "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
  "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco",
  "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia",
  "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger",
  "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan",
  "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
  "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines",
  "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
  "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan",
  "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo",
  "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe"
];

export default function Profile() {
  const { username } = useParams(); // Get username from URL
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [usernamePrompt, setUsernamePrompt] = useState("");
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [userStats, setUserStats] = useState({ versusLiveVotes: 0, soloVersusVotes: 0, battles: 0, wins: 0 });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const inputFileRef = useRef();
  const [isOwnProfile, setIsOwnProfile] = useState(false); // Track if viewing own profile

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchOrCreateProfile = async () => {
      setLoading(true);
      setError("");
      
      if (username) {
        // Viewing another user's profile by username
        try {
          const { data, error } = await supabase
            .from("profiles")
            .select("*")
            .ilike("username", username)
            .maybeSingle();
        
          if (error) {
            console.error('Error fetching profile:', error);
            setProfile(null);
            setLoading(false);
            return;
          }
        
          if (!data) {
            // Profile not found
            setProfile(null);
            setLoading(false);
            return;
          }
        
          setProfile(data);
          setForm(data || {});
          setIsOwnProfile(session?.user?.id === data?.id);
        
          // Set stats from profile data
          setUserStats({
            versusLiveVotes: data?.versus_live_votes || 0,
            soloVersusVotes: data?.solo_versus_votes || 0,
            battles: Math.floor(Math.random() * 100) + 20,
            wins: Math.floor(Math.random() * 50) + 10,
          });
        
          setLoading(false);
          return;
        } catch (err) {
          console.error('Unexpected error:', err);
          setProfile(null);
          setLoading(false);
          return;
        }
      }
      
      // Default behavior - view own profile
      if (!session?.user) {
        setProfile(null);
        setIsOwnProfile(false);
        setLoading(false);
        return;
      }
      
      try {
        let { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();
        
        if (error && error.code !== "PGRST116") {
          console.error('Error fetching own profile:', error);
          setProfile(null);
          setIsOwnProfile(true);
          setLoading(false);
          return;
        }
        
        if (!data) {
          // No profile found, need to create one
          setProfile(null);
          setIsOwnProfile(true);
          setLoading(false);
          setUsernamePrompt("");
          return;
        }
        
        setProfile(data);
        setForm(data || {});
        setIsOwnProfile(true);
        
        // Set stats from profile data
        setUserStats({
          versusLiveVotes: data?.versus_live_votes || 0,
          soloVersusVotes: data?.solo_versus_votes || 0,
          battles: Math.floor(Math.random() * 100) + 20,
          wins: Math.floor(Math.random() * 50) + 10,
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Unexpected error fetching own profile:', err);
        setProfile(null);
        setIsOwnProfile(true);
        setLoading(false);
      }
    };
    
    fetchOrCreateProfile();
  }, [session, username]);

  // Handle username prompt and profile creation if missing
  const handleCreateProfile = async () => {
    setError("");
    if (!usernamePrompt || usernamePrompt.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    const { data: existing } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", usernamePrompt)
      .single();
    if (existing) {
      setError("Username is already taken.");
      return;
    }
    const { error } = await supabase.from("profiles").insert([
      { id: session.user.id, username: usernamePrompt }
    ]);
    if (error) {
      setError(error.message);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setProfile(data);
    setForm(data || {});
    setUsernamePrompt("");
    setError("");
  };

  const handleEdit = () => setEditMode(true);

  const handleCancel = () => {
    setEditMode(false);
    setForm(profile);
    setError("");
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setError("");
    if (!form.username || form.username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (form.username !== profile.username) {
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", form.username)
        .single();
      if (existing) {
        setError("Username is already taken.");
        return;
      }
    }
    const { error } = await supabase
      .from("profiles")
      .update({
        username: form.username,
        avatar_url: form.avatar_url,
        bio: form.bio,
        user_country: form.user_country,
      })
      .eq("id", session.user.id);
    if (error) {
      setError(error.message);
      return;
    }
    setProfile({ ...profile, ...form });
    setEditMode(false);
  };

  // Avatar upload and crop logic
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setAvatarDialogOpen(true);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  // Add this utility function near the top:
  function resizeToSquare(file, size = 200) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(new File([blob], file.name, { type: "image/webp" }));
            else reject(new Error("Resize failed"));
          },
          "image/webp",
          undefined // lossless
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  const handleAvatarSave = async () => {
    if (!avatarFile || !croppedAreaPixels) return;
    try {
      // Crop and convert the image (webp preferred, png fallback)
      const { blob, ext, mime } = await getCroppedImg(avatarPreview, croppedAreaPixels);

      // Resize to 200x200 pixels (square)
      const resizedBlob = await resizeToSquare(blob, 200);

      const fileName = `${session.user.id}_avatar.webp`;
      const filePath = `images/user_avatars/${fileName}`;

      // Upload to Supabase Storage (replace if exists)
      const { error: uploadError } = await supabase.storage
        .from("manga_versus")
        .upload(filePath, resizedBlob, {
          upsert: true,
          contentType: "image/webp",
        });

      if (uploadError) {
        setError("Failed to upload avatar: " + uploadError.message);
        return;
      }

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from("manga_versus")
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrlData.publicUrl })
        .eq("id", session.user.id);

      if (updateError) {
        setError("Failed to update profile: " + updateError.message);
        return;
      }

      setForm((f) => ({ ...f, avatar_url: publicUrlData.publicUrl }));
      setProfile((p) => ({ ...p, avatar_url: publicUrlData.publicUrl }));
      setAvatarDialogOpen(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      setCroppedAreaPixels(null);
    } catch (err) {
      setError("Failed to process avatar image.");
    }
  };

  // When avatar is clicked in edit mode, open file picker
  const handleAvatarClick = () => {
    if (editMode && isOwnProfile && inputFileRef.current) {
      inputFileRef.current.value = null;
      inputFileRef.current.click();
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              border: "4px solid #f49330",
              borderTop: "4px solid transparent",
              borderRadius: "50%",
            }}
          />
        </motion.div>
        <Typography variant="h6" sx={{ color: "#fff", ml: 3, fontWeight: 600 }}>
          Loading your profile...
        </Typography>
      </Box>
    );
  }

  // Update the "not logged in" condition to only apply when viewing own profile
  if (!session?.user && !username) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <Paper
          sx={{
            p: 6,
            borderRadius: 4,
            background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
            border: "2px solid #3e3e3e",
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 2 }}>
            Authentication Required
          </Typography>
          <Typography sx={{ color: "#999", mb: 3 }}>
            Please log in to view your profile and track your battle statistics.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setAuthModalOpen(true)}
            sx={{
              py: 2,
              px: 4,
              fontSize: 16,
              fontWeight: 700,
              borderRadius: 3,
              background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
              color: "#181818",
              boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
              "&:hover": {
                background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Login / Sign Up
          </Button>
        </Paper>

        {/* Auth Modal */}
        <AuthModal 
          open={authModalOpen} 
          onClose={() => setAuthModalOpen(false)} 
        />
      </Box>
    );
  }

  // Update the profile creation condition to only apply when viewing own profile
  if (!profile && !username && session?.user) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2
      }}>
        <Paper
          sx={{
            p: 6,
            borderRadius: 4,
            background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
            border: "2px solid #3e3e3e",
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 2 }}>
            Create Your Profile
          </Typography>
          <Typography sx={{ color: "#999", mb: 3 }}>
            Choose a unique username to create your profile and start tracking your battle stats!
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter username"
            value={usernamePrompt}
            onChange={(e) => setUsernamePrompt(e.target.value)}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#1a1a1a",
                "& fieldset": { borderColor: "#3e3e3e" },
                "&:hover fieldset": { borderColor: "#f49330" },
                "&.Mui-focused fieldset": { borderColor: "#f49330" },
              },
              "& .MuiInputBase-input": { color: "#fff" },
            }}
          />
          <Button
            variant="contained"
            onClick={handleCreateProfile}
            disabled={!usernamePrompt.trim()}
            sx={{
              py: 2,
              px: 4,
              fontSize: 16,
              fontWeight: 700,
              borderRadius: 3,
              background: usernamePrompt.trim() 
                ? "linear-gradient(45deg, #f49330 30%, #fff176 90%)"
                : "#333",
              color: usernamePrompt.trim() ? "#181818" : "#666",
              boxShadow: usernamePrompt.trim() 
                ? "0 4px 20px rgba(244, 147, 48, 0.4)" 
                : "none",
              "&:hover": usernamePrompt.trim() ? {
                background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                transform: "translateY(-2px)",
              } : {},
              transition: "all 0.3s ease",
            }}
          >
            Create Profile
          </Button>
          {error && (
            <Typography sx={{ color: "#ff5252", mt: 2, fontSize: 14 }}>
              {error}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  // Update the "profile not found" condition (around line 310) to this:
  if (!profile && username) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2
      }}>
        <Paper
          sx={{
            p: 6,
            borderRadius: 4,
            background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
            border: "2px solid #ff5252",
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 2 }}>
            Profile Not Found
          </Typography>
          <Typography sx={{ color: "#999", mb: 3 }}>
            The user "{username}" doesn't exist or hasn't created a profile yet.
          </Typography>
          <Button
            variant="outlined"
            href="/profile"
            sx={{
              borderColor: "#f49330",
              color: "#f49330",
              fontWeight: 600,
              "&:hover": {
                background: "rgba(244, 147, 48, 0.1)",
                borderColor: "#fff176",
                color: "#fff176",
              },
            }}
          >
            Go to Your Profile
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      py: 8
    }}>
      {/* Spacer for navbar */}
      <Box sx={{ height: 80 }} />
      
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Profile Header Card */}
          <Paper
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
              border: "2px solid #3e3e3e",
              mb: 4,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          >
            {/* Banner */}
            <Box
              sx={{
                height: 250,
                background: profile?.banner_image_url
                  ? `url(${profile.banner_image_url}) center/cover`
                  : "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                position: "relative",
                display: "flex",
                alignItems: "flex-end",
                padding: 4,
              }}
            >
              {/* Avatar */}
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    width: 150,
                    height: 150,
                    border: "6px solid #181818",
                    background: "#333",
                    borderRadius: "50%",
                    overflow: "hidden",
                    fontSize: 60,
                    fontWeight: 700,
                    cursor: editMode ? "pointer" : "default",
                    transition: "all 0.3s ease",
                    boxShadow: editMode ? "0 0 0 4px #f49330" : "0 8px 32px rgba(0, 0, 0, 0.5)",
                    "&:hover": editMode ? {
                      transform: "scale(1.05)",
                    } : {},
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={handleAvatarClick}
                >
                  {profile?.avatar_url ? (
                    <LazyImage
                      src={profile.avatar_url}
                      alt={profile?.username || "Avatar"}
                      threshold={0.2}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    <PersonIcon sx={{ fontSize: 80, color: "#888" }} />
                  )}
                </Box>
                {editMode && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        background: "#f49330",
                        borderRadius: "50%",
                        p: 1,
                        cursor: "pointer",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
                      }}
                      onClick={handleAvatarClick}
                    >
                      <CameraAltIcon sx={{ color: "#181818", fontSize: 24 }} />
                    </Box>
                  </motion.div>
                )}
              </Box>
            </Box>

            {/* Profile Info */}
            <Box sx={{ p: 4 }}>
              <AnimatePresence mode="wait">
                {editMode ? (
                  <motion.div
                    key="edit"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {/* Username and Country Row */}
                      <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
                        <Box sx={{ flex: "0 0 300px" }}>
                          <TextField
                            label="Username"
                            name="username"
                            value={form.username || ""}
                            onChange={handleChange}
                            required
                            fullWidth
                            sx={{
                              "& .MuiInputBase-root": {
                                background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                                borderRadius: 3,
                              },
                              "& .MuiInputBase-input": {
                                color: "#fff",
                                fontSize: 16,
                              },
                              "& .MuiInputLabel-root": {
                                color: "#999",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#f49330",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#555",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#f49330",
                              },
                              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#f49330",
                              },
                            }}
                          />
                        </Box>
                        
                        <Box sx={{ flex: "1 1 450px" }}>
                          <Autocomplete
                            options={countries}
                            value={form.user_country || ""}
                            onChange={(event, newValue) => {
                              setForm(f => ({ ...f, user_country: newValue || "" }));
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Country"
                                fullWidth
                                sx={{
                                  "& .MuiInputBase-root": {
                                    background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                                    borderRadius: 3,
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "#fff",
                                    fontSize: 16,
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: "#999",
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#f49330",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#555",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#f49330",
                                  },
                                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#f49330",
                                  },
                                }}
                              />
                            )}
                            PaperComponent={(props) => (
                              <Box
                                {...props}
                                sx={{
                                  background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
                                  border: "2px solid #3e3e3e",
                                  borderRadius: 3,
                                  maxHeight: 300,
                                  "& .MuiAutocomplete-option": {
                                    color: "#fff",
                                    "&:hover": {
                                      background: "rgba(244, 147, 48, 0.1)",
                                    },
                                    '&[aria-selected="true"]': {
                                      background: "rgba(244, 147, 48, 0.2)",
                                    },
                                  },
                                }}
                              />
                            )}
                            sx={{
                              "& .MuiAutocomplete-clearIndicator": {
                                color: "#999",
                                "&:hover": {
                                  color: "#f49330",
                                },
                              },
                              "& .MuiAutocomplete-popupIndicator": {
                                color: "#999",
                                "&:hover": {
                                  color: "#f49330",
                                },
                              },
                            }}
                          />
                        </Box>
                      </Box>
                      
                      {/* Bio Field - Full Width */}
                      <Box>
                        <TextField
                          label="Bio"
                          name="bio"
                          value={form.bio || ""}
                          onChange={handleChange}
                          multiline
                          rows={4}
                          fullWidth
                          placeholder="Tell us about your favorite manga, characters, and battle preferences..."
                          inputProps={{ maxLength: 300 }}
                          helperText={`${(form.bio || "").length}/300 characters`}
                          sx={{
                            "& .MuiInputBase-root": {
                              background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                              borderRadius: 3,
                            },
                            "& .MuiInputBase-input": {
                              color: "#fff",
                              fontSize: 16,
                            },
                            "& .MuiInputLabel-root": {
                              color: "#999",
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#f49330",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#555",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#f49330",
                            },
                            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#f49330",
                            },
                            "& .MuiFormHelperText-root": {
                              color: "#999",
                              fontSize: 12,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <Typography 
                          sx={{ 
                            color: "#ff5252", 
                            mt: 3, 
                            p: 2, 
                            background: "rgba(255, 82, 82, 0.1)",
                            borderRadius: 2,
                            border: "1px solid rgba(255, 82, 82, 0.3)",
                            textAlign: "center"
                          }}
                        >
                          {error}
                        </Typography>
                      </motion.div>
                    )}
                    
                    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                          py: 1.5,
                          px: 4,
                          fontSize: 16,
                          fontWeight: 700,
                          borderRadius: 3,
                          background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                          color: "#181818",
                          boxShadow: "0 4px 20px rgba(244, 147, 48, 0.4)",
                          "&:hover": {
                            background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                            boxShadow: "0 6px 25px rgba(244, 147, 48, 0.6)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Save Changes
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        sx={{
                          py: 1.5,
                          px: 4,
                          fontSize: 16,
                          fontWeight: 700,
                          borderRadius: 3,
                          borderColor: "#555",
                          color: "#fff",
                          "&:hover": {
                            borderColor: "#f49330",
                            color: "#f49330",
                            background: "rgba(244, 147, 48, 0.1)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </motion.div>
                ) : (
                  <motion.div
                    key="view"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                      <Box>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 900, 
                            color: "#fff",
                            mb: 1,
                            fontSize: { xs: 32, md: 40 }
                          }}
                        >
                          {profile?.username}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          {profile?.user_country && (
                            <Chip
                              icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
                              label={profile.user_country}
                              sx={{
                                background: "rgba(244, 147, 48, 0.1)",
                                color: "#f49330",
                                border: "1px solid rgba(244, 147, 48, 0.3)",
                                fontWeight: 600,
                              }}
                            />
                          )}
                          <Chip
                            icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
                            label={`Joined ${profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "Unknown"}`}
                            sx={{
                              background: "rgba(99, 102, 241, 0.1)",
                              color: "#6366f1",
                              border: "1px solid rgba(99, 102, 241, 0.3)",
                              fontWeight: 600,
                            }}
                          />
                        </Stack>
                      </Box>
                      {isOwnProfile && (
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={handleEdit}
                          sx={{
                            borderColor: "#f49330",
                            color: "#f49330",
                            fontWeight: 600,
                            borderRadius: 3,
                            px: 3,
                            "&:hover": {
                              background: "rgba(244, 147, 48, 0.1)",
                              borderColor: "#fff176",
                              color: "#fff176",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Edit Profile
                        </Button>
                      )}
                    </Stack>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: "#ddd", 
                        fontSize: 16, 
                        lineHeight: 1.6,
                        fontStyle: profile?.bio ? "normal" : "italic",
                        opacity: profile?.bio ? 1 : 0.7
                      }}
                    >
                      {profile?.bio || "This warrior hasn't shared their battle story yet. Add a bio to tell others about your favorite manga and characters!"}
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
                    border: "2px solid #f49330",
                    borderRadius: 4,
                    boxShadow: "0 8px 32px rgba(244, 147, 48, 0.2)",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 900, 
                        color: "#f49330",
                        mb: 1,
                        fontSize: 48
                      }}
                    >
                      {userStats.versusLiveVotes}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
                      Versus Live Votes
                    </Typography>
                    <Typography sx={{ color: "#999", mt: 1, fontSize: 14 }}>
                      Live battle participation
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Card
                  sx={{
                    background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
                    border: "2px solid #6366f1",
                    borderRadius: 4,
                    boxShadow: "0 8px 32px rgba(99, 102, 241, 0.2)",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 900, 
                        color: "#6366f1",
                        mb: 1,
                        fontSize: 48
                      }}
                    >
                      {userStats.soloVersusVotes}
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
                      Solo Versus Votes
                    </Typography>
                    <Typography sx={{ color: "#999", mt: 1, fontSize: 14 }}>
                      Personal voting sessions
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Favorite Characters Section */}
          <Paper
            sx={{
              borderRadius: 4,
              background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
              border: "2px solid #3e3e3e",
              p: 4,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: "#fff", 
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 2
              }}
            >
              <FavoriteIcon sx={{ color: "#e91e63" }} />
              Favorite Characters
            </Typography>
            <Box 
              sx={{ 
                textAlign: "center", 
                py: 6,
                color: "#999",
                borderRadius: 3,
                background: "rgba(0, 0, 0, 0.2)",
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Coming Soon!
              </Typography>
              <Typography>
                Showcase your most beloved manga characters and build your personal collection.
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      {/* Avatar Crop Dialog */}
      <Dialog 
        open={avatarDialogOpen} 
        onClose={() => setAvatarDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
            border: "2px solid #f49330",
            borderRadius: 4,
          }
        }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 700, fontSize: 24 }}>
          Crop Your Avatar
        </DialogTitle>
        <DialogContent>
          {avatarPreview && (
            <Box sx={{ position: "relative", width: "100%", height: 400, background: "#181818", borderRadius: 3, overflow: "hidden" }}>
              <Cropper
                image={avatarPreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: {
                    borderRadius: 12,
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setAvatarDialogOpen(false)}
            sx={{ 
              color: "#999",
              "&:hover": { color: "#fff" }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAvatarSave}
            sx={{
              background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
              color: "#181818",
              fontWeight: 700,
              px: 4,
              "&:hover": {
                background: "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
              },
            }}
          >
            Save Avatar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input for avatar upload */}
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        ref={inputFileRef}
        onChange={handleAvatarUpload}
      />
    </Box>
  );
}